import { Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/lib/utils";

import {
  PAYMENT_METHOD_LABELS,
  VERIFICATION_STATUS_LABELS,
} from "./receivable-labels";

import type { Receivable, ReceivablePayment } from "@artisancode/api-types";

export function ReceivablePaymentHistory({
  payments,
  canUpdate,
  canDelete,
  canVerify,
  toggleVerification,
  removePayment,
}: {
  payments: Receivable["payments"];
  canUpdate: boolean;
  canDelete: boolean;
  /** receivables.verify: needed to verify, unverify, or delete a verified payment. */
  canVerify: boolean;
  toggleVerification: (payment: ReceivablePayment) => void;
  removePayment: (paymentId: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold">Riwayat Pembayaran</Label>
      {payments.length === 0 ? (
        <p className="py-1 text-xs text-muted-foreground">
          Belum ada pembayaran tercatat.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table className="min-w-[580px]">
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>No. Bayar</TableHead>
                <TableHead>Nominal</TableHead>
                <TableHead>Metode</TableHead>
                <TableHead>Verifikasi</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="text-xs">{p.date}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {p.code ?? "-"}
                  </TableCell>
                  <TableCell className="text-xs font-medium">
                    {formatCurrency(p.amount)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {PAYMENT_METHOD_LABELS[
                      p.method as keyof typeof PAYMENT_METHOD_LABELS
                    ] ?? p.method}
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      disabled={!canUpdate || !canVerify}
                      onClick={() => toggleVerification(p)}
                      className="disabled:cursor-not-allowed"
                    >
                      <Badge
                        variant={
                          p.verificationStatus === "verified"
                            ? "default"
                            : "outline"
                        }
                        className="text-[10px]"
                      >
                        {VERIFICATION_STATUS_LABELS[p.verificationStatus]}
                      </Badge>
                    </button>
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={
                        !canDelete ||
                        (p.verificationStatus === "verified" && !canVerify)
                      }
                      onClick={() => removePayment(p.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
